/**
 * Core HTML parsing engine for building AST and block index
 */

import { AstNode, ParsedDocument, TextRunLookup } from '../types';
import { processDElementChildren, processChildNode } from './nodeTransforms';
/**
 * Normalizes HTML string by handling <d/> elements
 * Converts self-closing <d/> to <d></d> to ensure proper parsing
 */
export function normalizeHtml(htmlString: string): string {
  return htmlString.replace(/<d\s*\/>/g, '<d></d>');
}

/**
 * Main parsing function: converts HTML string to AST with block index
 * This is the single-pass transformation that builds both the AST and block index
 */
export function parseHtml(htmlString: string): ParsedDocument {
  const normalizedHtml = normalizeHtml(htmlString);
  const parser = new DOMParser();
  const doc = parser.parseFromString(normalizedHtml, 'text/html');

  const textRunLookup: TextRunLookup = new Map();
  const runId = { current: 0 };

  const ast: AstNode[] = [];
  const bodyChildren = Array.from(doc.body.childNodes);

  for (let i = 0; i < bodyChildren.length; i++) {
    const child = bodyChildren[i];
    const path = [i];

    if (
      child.nodeType === Node.ELEMENT_NODE &&
      (child as Element).nodeName.toLowerCase() === 'd'
    ) {
      // Handle <d> elements at root level
      const dChildren = processDElementChildren(
        child as Element,
        path,
        runId,
        textRunLookup,
      );
      ast.push(...dChildren);
    } else {
      const childAst = processChildNode(child, path, runId, textRunLookup);
      if (childAst) {
        ast.push(childAst);
      }
    }
  }

  return {
    ast,
    textRunLookup,
  };
}
